'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  getDataAttributes,
  isBrowser,
} from '@wix/editor-elements-common-utils';
import { useDomDirection } from '@wix/editor-elements-common-utils/hooks';
import type { ISliderImperativeActions, ISliderProps } from '../Slider.types';
import { CONTINUOUS_STEP, TestIds, Sizes } from '../constants';
import { st, classes } from './Slider.st.css';
import Ticks from './Ticks/Ticks';

const Slider: React.ForwardRefRenderFunction<
  ISliderImperativeActions,
  ISliderProps
> = (props, ref) => {
  const {
    id,
    className,
    isDisabled,
    width,
    height,
    min,
    max,
    step,
    value,
    thumbShape,
    thumbSize,
    tickMarksShape,
    tickMarksPosition,
    tooltipVisibility,
    tooltipPosition,
    tooltipPrefix,
    tooltipSuffix,
    stepType,
    readOnly,
    label,
    labelMargin,
    onChange,
    updatePropsValue,
    onMouseEnter,
    onMouseLeave,
    onFocus = () => {},
    onBlur = () => {},
    onClick,
    onDblClick,
    ariaAttributes,
    lang,
  } = props;
  const { direction: trackDirection, directionRef: trackRef } =
    useDomDirection<HTMLDivElement>();

  const thumbRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(ref, () => ({
    focus() {
      thumbRef.current?.focus();
    },
    blur() {
      thumbRef.current?.blur();
    },
  }));

  const [dragging, setDragging] = React.useState(false);
  const [mouseDown, setMouseDown] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [thumbHover, setThumbHover] = React.useState(false);
  const [inKeyPress, setInKeyPress] = React.useState(false);
  const [labelHeight, setLabelHeight] = React.useState(0);

  const labelRef: React.RefObject<HTMLLabelElement> = React.createRef();

  React.useEffect(() => {
    let newLabelHeight = 0;

    if (label && labelRef.current) {
      const labelTextHeight = labelRef.current.getBoundingClientRect().height;
      newLabelHeight = labelMargin + labelTextHeight;
    }
    setLabelHeight(newLabelHeight);
  }, [label, labelRef, labelMargin]);

  const thumbOffset = React.useMemo(() => {
    if (!step || tickMarksShape === 'none' || tickMarksPosition === 'middle') {
      return 0;
    }
    const tickHeight =
      tickMarksShape === 'line' ? Sizes.tickLineHeight : Sizes.tickDotSize;
    return tickHeight + Sizes.tickMarksGap;
  }, [step, tickMarksShape, tickMarksPosition]);

  const thumbSizes = React.useMemo(() => {
    const shapeWidthMult = { circle: 1, square: 1, rectangle: 1.5, bar: 0.5 };

    const baseThumbHeight = Math.min(width, height);
    const baseThumbWidth = shapeWidthMult[thumbShape] * baseThumbHeight;
    if (thumbSize) {
      return {
        thumbHeight: thumbSize,
        thumbWidth: shapeWidthMult[thumbShape] * thumbSize,
      };
    }

    return {
      thumbHeight: baseThumbHeight - thumbOffset - labelHeight,
      thumbWidth: baseThumbWidth - thumbOffset - labelHeight,
    };
  }, [thumbOffset, width, height, thumbShape, thumbSize, labelHeight]);

  const rootStyles = React.useMemo(
    () => ({
      '--thumbWidth': `${thumbSizes.thumbWidth}px`,
      '--thumbHeight': `${thumbSizes.thumbHeight}px`,
      '--ticksWrapperHeight': `${thumbOffset}px`,
      ...(step &&
        tickMarksShape !== 'none' &&
        tickMarksPosition === 'across' && {
          '--innerTop': `${thumbOffset}px`,
        }),
    }),
    [thumbSizes, tickMarksShape, step, thumbOffset, tickMarksPosition],
  );

  const stepValue = React.useMemo(() => {
    if (!step || step < 0) {
      return CONTINUOUS_STEP;
    }
    if (stepType === 'count') {
      return (max - min) / step;
    }
    return step;
  }, [min, max, step, stepType]);

  const totalSteps = React.useMemo(() => {
    return Math.ceil((max - min) / stepValue);
  }, [min, max, stepValue]);

  const clampedPercent = React.useMemo(() => {
    const pct = (value - min) / (max - min);
    return Math.min(Math.max(pct, 0), 1);
  }, [value, min, max]);

  const shouldShowTooltip = React.useMemo(() => {
    if (tooltipVisibility !== 'hover') {
      return tooltipVisibility === 'always';
    }
    return dragging || thumbHover || inKeyPress || focused;
  }, [tooltipVisibility, dragging, thumbHover, inKeyPress, focused]);

  const clampedValue = React.useMemo(() => {
    return Math.floor(10 * value) / 10;
  }, [value]);

  const maxTickDensity = 1 / (tickMarksShape === 'line' ? 8 : 16);

  const handleBlur = (event: React.FocusEvent) => {
    setInKeyPress(false);
    setFocused(false);
    onBlur(event);
  };

  const handleThumbEnter = () => setThumbHover(true);

  const handleThumbLeave = () => setThumbHover(false);

  const handleChange = React.useCallback(
    (
      newValue: number,
      event:
        | React.MouseEvent
        | React.TouchEvent
        | React.KeyboardEvent
        | MouseEvent
        | TouchEvent
        | KeyboardEvent,
    ) => {
      const changedValue =
        Math.floor(100 * Math.min(Math.max(newValue, min), max)) / 100;
      if (changedValue !== value) {
        updatePropsValue(changedValue);
        onChange?.(event);
      }
    },
    [max, min, onChange, updatePropsValue, value],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isDisabled || readOnly) {
      return;
    }

    const changeByKey = (newValue: number) => {
      handleChange(newValue, event);
      setInKeyPress(true);
      event.preventDefault();
    };

    const handleEscapePress = () => {
      thumbRef.current?.blur();
    };

    switch (event.key) {
      case 'Escape':
        handleEscapePress();
        return;
      case 'ArrowUp':
        return changeByKey(value + stepValue);
      case 'ArrowDown':
        return changeByKey(value - stepValue);
      case 'ArrowLeft':
        return changeByKey(
          value + (trackDirection === 'rtl' ? stepValue : -stepValue),
        );
      case 'ArrowRight':
        return changeByKey(
          value + (trackDirection === 'rtl' ? -stepValue : stepValue),
        );
      case 'PageUp':
        return changeByKey(value + 2 * stepValue);
      case 'PageDown':
        return changeByKey(value - 2 * stepValue);
      case 'Home':
        return changeByKey(min);
      case 'End':
        return changeByKey(max);
      default:
    }
  };

  const moveThumbByMouse = (
    event: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent,
    clientX: number,
  ) => {
    if (isDisabled || readOnly) {
      return;
    }
    const rect = trackRef.current!.getBoundingClientRect();
    let sliderPos;

    if (trackDirection === 'rtl') {
      sliderPos = rect.left + rect.width - thumbSizes.thumbWidth / 2 - clientX;
    } else {
      sliderPos = clientX - (rect.left + thumbSizes.thumbWidth / 2);
    }

    const pxStep = (rect.width - thumbSizes.thumbWidth) / totalSteps;
    const newValue = min + stepValue * Math.round(sliderPos / pxStep);
    handleChange(newValue, event);
  };

  const handleMouseDown = () => {
    setMouseDown(true);
  };

  const handleMouseUp = () => {
    setMouseDown(false);
    setDragging(false);
  };

  const handleMouseMove = (event: MouseEvent | React.MouseEvent) => {
    if (mouseDown && !dragging) {
      setDragging(true);
    }
    if (!dragging) {
      return;
    }
    moveThumbByMouse(event, event.clientX);
  };

  const handleTouchMove = (event: TouchEvent | React.TouchEvent) => {
    if (mouseDown && !dragging) {
      setDragging(true);
    }
    if (!dragging) {
      return;
    }
    event.preventDefault();
    moveThumbByMouse(event, event.touches[0].clientX);
  };

  const onTickClick = React.useCallback(moveThumbByMouse, [
    trackDirection,
    trackRef,
    isDisabled,
    handleChange,
    min,
    readOnly,
    stepValue,
    thumbSizes.thumbWidth,
    totalSteps,
  ]);

  React.useEffect(() => {
    if (!isBrowser()) {
      return;
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  });

  const labelOnClick: React.MouseEventHandler<HTMLLabelElement> = () => {
    thumbRef.current?.focus();
  };

  const _onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
    setFocused(true);
    onFocus(event);
  };

  const ariaProps = Object.fromEntries(
    Object.entries({
      'aria-label': ariaAttributes?.label,
    }).filter(([, ariaValue]) => ariaValue !== undefined),
  );

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(className, classes.rootWrapper)}
      lang={lang}
    >
      {label && (
        <label
          id={`label_${id}`}
          htmlFor={`slider_${id}`}
          className={classes.label}
          ref={labelRef}
          onClick={labelOnClick}
        >
          {label}
        </label>
      )}
      <div
        id={`slider_${id}`}
        className={st(classes.root, {
          disabled: isDisabled,
        })}
        onClick={onClick}
        onDoubleClick={onDblClick}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...ariaProps}
        style={rootStyles as React.CSSProperties}
        data-testid={TestIds.root}
      >
        <div className={classes.inner}>
          <div
            data-testid={TestIds.track}
            ref={trackRef}
            className={classes.track}
            onClick={(event: React.MouseEvent) =>
              moveThumbByMouse(event, event.clientX)
            }
          >
            <div
              className={classes.trackFill}
              style={
                {
                  '--trackFillPercent': clampedPercent,
                } as React.CSSProperties
              }
            />
          </div>
          <div
            ref={thumbRef}
            data-testid={TestIds.thumb}
            className={classes.thumb}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-labelledby={label && `label_${id}`}
            onMouseEnter={handleThumbEnter}
            onMouseLeave={handleThumbLeave}
            onFocus={_onFocus}
            onBlur={handleBlur}
            tabIndex={0}
            style={
              {
                '--thumbPercent': clampedPercent,
              } as React.CSSProperties
            }
          >
            <div className={classes.thumbShape} />
            {shouldShowTooltip && (
              <div
                data-testid={TestIds.tooltip}
                className={st(classes.tooltip, { position: tooltipPosition })}
              >
                {tooltipPrefix}
                {clampedValue}
                {tooltipSuffix}
              </div>
            )}
          </div>
        </div>
        {tickMarksShape !== 'none' && !!step && (
          <Ticks
            step={stepValue}
            min={min}
            max={max}
            maxTickDensity={maxTickDensity}
            trackSize={width - thumbSizes.thumbHeight}
            onTickClick={onTickClick}
            tickMarksPosition={tickMarksPosition}
          />
        )}
      </div>
    </div>
  );
};

export default React.forwardRef(Slider);
